/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEnrolledStatus } from "@/types/employeeState";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWizardProgress } from "../../../../hooks/useWizardProgress";
import {
  createBeneficiaries,
  fetchEmployeeData,
  removeBeneficiary,
  updateBeneficiaries,
} from "../../../../services/beneficiaries";
import { RootState } from "../../../../store";
import {
  addBeneficiary,
  deleteBeneficiaryFromStore,
  updateBeneficiary,
} from "../../../../store/wizard";
import { Beneficiary } from "../../../../types/beneficiaries";
import { handleApiError } from "../../../../utils/errorHandling";
import { formatDate } from "../../../../utils/generalUtilities";
import { customToast } from "../../../Common/Toast";
import {
  createEmptyBeneficiary,
  detectBeneficiaryChanges,
  hasValidBeneficiaries,
  isNewBeneficiary,
} from "../utils/beneficiaryUtils";

interface UseBeneficiaryManagementReturn {
  beneficiaries: Beneficiary[];
  setBeneficiaries: React.Dispatch<React.SetStateAction<Beneficiary[]>>;
  initialBeneficiariesFromAPI: Beneficiary[];
  loading: boolean;
  isSubmitting: boolean;
  error: string | null;
  isSuccess: boolean;
  handleFetchBeneficiariesFromAPI: () => Promise<void>;
  handleAddBeneficiary: () => void;
  handleCreateBeneficiaries: (
    newBeneficiaries: Beneficiary[],
  ) => Promise<boolean>;
  handleUpdateBeneficiaries: (
    updatedBeneficiaries: Beneficiary[],
  ) => Promise<boolean>;
  handleDeleteBeneficiary: (beneficiaryToDelete: Beneficiary) => Promise<void>;
  processAddOrUpdateBeneficiaries: (
    beneficiariesToProcess: Beneficiary[],
  ) => Promise<boolean>;
}

export const useBeneficiaryManagement = (): UseBeneficiaryManagementReturn => {
  const dispatch = useDispatch();
  const { id: userAccountID } = useSelector((state: RootState) => state.user);
  const { resetProgress } = useWizardProgress();

  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  const [initialBeneficiariesFromAPI, setInitialBeneficiariesFromAPI] =
    useState<Beneficiary[]>([]);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([
    createEmptyBeneficiary(),
  ]);

  const handleFetchBeneficiariesFromAPI = async () => {
    try {
      setError(null);
      setLoading(true);

      const response = await fetchEmployeeData();

      const filteredBeneficiaries = response.data
        .filter((item: any) => !isEnrolledStatus(item.enrolledStatus))
        .map((item: any) => ({
          id: item.minorAccountID,
          firstName: item.firstName,
          lastName: item.lastName,
          middleName: item.middleName || "",
          relationship: item.relationship as
            | "Child"
            | "ChildOfCoworker"
            | "Grandchild"
            | "NieceOrNephew"
            | "Other"
            | "",
          dateOfBirth: formatDate(item.dateOfBirth),
        }));

      if (filteredBeneficiaries.length !== 0) {
        setBeneficiaries(filteredBeneficiaries);
        setInitialBeneficiariesFromAPI(filteredBeneficiaries);
      }
    } catch {
      setError("Unable to load beneficiaries. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBeneficiary = () => {
    setBeneficiaries((prev) => [...prev, createEmptyBeneficiary()]);
  };

  const handleCreateBeneficiaries = async (
    newBeneficiaries: Beneficiary[],
  ): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      let allSuccessful = true;

      const response = await createBeneficiaries(newBeneficiaries, {
        userAccountID,
      });

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          "Invalid response format from create beneficiaries API",
        );
      }

      response.data.forEach((result, index) => {
        if (result.status === 200) {
          const beneficiary = {
            ...newBeneficiaries[index],
            id: result.res,
          };
          dispatch(addBeneficiary(beneficiary));
          customToast(
            `Successfully added ${beneficiary.firstName} as a beneficiary`,
            "success",
          );
        } else {
          allSuccessful = false;
          setIsSuccess(false);
          const errorMsg =
            result.message ||
            `Failed to add ${newBeneficiaries[index].firstName}`;
          customToast(`${errorMsg} (Status: ${result.status})`, "error");
        }
      });

      return allSuccessful;
    } catch (error) {
      setIsSuccess(false);
      handleApiError(
        error,
        "Failed to create new beneficiaries",
        true,
        customToast,
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBeneficiaries = async (
    updatedBeneficiaries: Beneficiary[],
  ): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      let allSuccessful = true;

      const response = await updateBeneficiaries(updatedBeneficiaries, {
        userAccountID,
      });

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          "Invalid response format from update beneficiaries API",
        );
      }

      response.data.forEach((result, index) => {
        if (result.status === 200) {
          const beneficiary = updatedBeneficiaries[index];
          dispatch(updateBeneficiary(beneficiary));
          customToast(
            `Successfully updated ${beneficiary.firstName}`,
            "success",
          );
        } else {
          allSuccessful = false;
          setIsSuccess(false);
          const errorMsg =
            result.message ||
            `Failed to update ${updatedBeneficiaries[index].firstName}`;
          customToast(`${errorMsg} (Status: ${result.status})`, "error");
        }
      });

      return allSuccessful;
    } catch (error) {
      setIsSuccess(false);
      handleApiError(
        error,
        "Failed to update beneficiary information",
        true,
        customToast,
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetProgress = (remainingBeneficiaries: Beneficiary[]) => {
    if (!hasValidBeneficiaries(remainingBeneficiaries)) {
      resetProgress();
    }
  };

  const handleDeleteBeneficiary = async (beneficiaryToDelete: Beneficiary) => {
    try {
      if (beneficiaryToDelete.id) {
        const response = await removeBeneficiary(
          beneficiaryToDelete.id.toString(),
          { userAccountID },
        );

        if (response?.status === 200) {
          setBeneficiaries((prev) => {
            const filtered = prev.filter(
              (b) => b.id !== beneficiaryToDelete.id,
            );
            const result: Beneficiary[] =
              filtered.length === 0 ? [createEmptyBeneficiary()] : filtered;
            handleResetProgress(result);
            return result;
          });

          dispatch(deleteBeneficiaryFromStore(beneficiaryToDelete.id));
          customToast(
            `Successfully removed ${beneficiaryToDelete.firstName} from beneficiaries`,
            "success",
          );
        } else {
          throw new Error(
            `Failed to remove beneficiary (Status: ${response?.status})`,
          );
        }
      } else {
        setBeneficiaries((prev) => {
          const indexToRemove = prev.findIndex(
            (b) =>
              b.firstName === beneficiaryToDelete.firstName &&
              b.lastName === beneficiaryToDelete.lastName &&
              b.relationship === beneficiaryToDelete.relationship &&
              b.dateOfBirth === beneficiaryToDelete.dateOfBirth,
          );

          if (indexToRemove !== -1) {
            const filtered = [...prev];
            filtered.splice(indexToRemove, 1);
            const result: Beneficiary[] =
              filtered.length === 0 ? [createEmptyBeneficiary()] : filtered;
            handleResetProgress(result);
            return result;
          }

          return prev;
        });
      }
    } catch (error) {
      handleApiError(
        error,
        `Failed to remove ${beneficiaryToDelete.firstName || "beneficiary"}`,
        true,
        customToast,
      );
    }
  };

  const processAddOrUpdateBeneficiaries = async (
    beneficiariesToProcess: Beneficiary[],
  ): Promise<boolean> => {
    try {
      setIsSuccess(true);
      let overallSuccess = true;

      const newBeneficiaries = beneficiariesToProcess.filter((b) =>
        isNewBeneficiary(b, initialBeneficiariesFromAPI),
      );
      const changedBeneficiaries = detectBeneficiaryChanges(
        beneficiariesToProcess,
        initialBeneficiariesFromAPI,
      );

      if (newBeneficiaries.length > 0) {
        const createSuccess = await handleCreateBeneficiaries(newBeneficiaries);
        if (createSuccess) {
          resetProgress();
        }
        if (!createSuccess) overallSuccess = false;
      }

      if (changedBeneficiaries.length > 0) {
        const updateSuccess =
          await handleUpdateBeneficiaries(changedBeneficiaries);
        if (!updateSuccess) overallSuccess = false;
      }

      return overallSuccess;
    } catch (error) {
      handleApiError(
        error,
        "Failed to process beneficiary changes",
        true,
        customToast,
      );
      return false;
    }
  };

  return {
    beneficiaries,
    setBeneficiaries,
    initialBeneficiariesFromAPI,
    loading,
    isSubmitting,
    error,
    isSuccess,
    handleFetchBeneficiariesFromAPI,
    handleAddBeneficiary,
    handleCreateBeneficiaries,
    handleUpdateBeneficiaries,
    handleDeleteBeneficiary,
    processAddOrUpdateBeneficiaries,
  };
};
